import Axios from "axios";
import { BookingFlowLayout } from "components/booking-flow";
import { Footer, Header } from "components";
import FilterOptionButton from "components/filter-option-button";
import useAnalytics from "hooks/analytics";
import useAuthUser from "hooks/auth-user";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useLanguage from "hooks/language";
import { AppointmentSummaryModal } from "modals";
import AppointmentNotAvailableModal from "modals/appointment-not-available";
import { FollowUpType } from "models/appointment";
import Clinic from "models/clinic";
import { SCREENING_FLOW } from "models/screening-flow";
import { getAppointmentNeedingMissedPayment } from "models/user";
import DateSelection from "pages/home/screening/date-selection";
import React, { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import { Redirect, useHistory, useLocation } from "react-router";
import { Translations } from "translations/types";
import {
    trackBookingFlowClinicListViewed,
    trackBookingFlowClinicSelected,
    trackBookingFlowConsultationSelected,
} from "utils/analytics/booking-flow-tracking";
import { OpenModalTrigger, UserActionEvent } from "utils/analytics/events";
import { APPOINTMENT_RESERVATION_CONFLICT } from "utils/api-error-types";
import { suppressErrorNotificationForTypes } from "utils/axios-error-notification";
import ENDPOINTS from "utils/endpoints";
import { normalizePostalCode } from "utils/format";
import { getLabType, LabType } from "utils/lab-type-helper";
import { setPreSelectedClinicID } from "utils/local-storage";
import routes from "utils/routes";
import {
    getPreSelectedLabType,
    getScreeningFlow,
    setAppointmentHold,
    getBookingFollowUpType,
    removeBookingFollowUpType,
    setBookingFollowUpType,
    setPreSelectedLabType,
} from "utils/session-storage";
import AvailabilityLocationModal, {
    LocationModalResult,
} from "./availabilityLocationModal";
import "./availabilityPage.css";
import ConsultationSelectionV2 from "./consultation-selection-v2";
import HpvServicePage from "./hpvServicePage";
import LabSelectionPage from "./labSelectionPage";

export type UserGeolocation = {
    latitude: number;
    longitude: number;
};

export type FetchClinicsBody = {
    originPostalCode?: string | null;
    geolocation?: UserGeolocation;
    isEngageAppointment: boolean;
    labType: LabType[];
};

export type LocationType = "postalCode" | "location";
type AvailabilityStep =
    | "labSelection"
    | "hpvService"
    | "consultation"
    | "availability";

type AvailabilityLocationState = {
    screeningFlowSwitched?: boolean;
};

export default function AvailabilityPage(): React.ReactElement {
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const screeningFlow = getScreeningFlow();
    const [authUser, setAuthUser] = useAuthUser();
    const { push, replace } = useHistory();
    const location = useLocation<AvailabilityLocationState>();
    const [clinics, setClinics] = useState<Clinic[] | undefined>();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(true);
    const [
        appointmentNotAvailableModalVisible,
        setAppointmentNotAvailableModalVisible,
    ] = useState(false);
    const [
        appointmentSummaryModalVisible,
        setAppointmentSummaryModalVisible,
    ] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState("");
    const [appointmentClinicId, setAppointmentClinicId] = useState<number>();
    const [fetchClinicsBody, setFetchClinicsBody] = useState<FetchClinicsBody>({
        isEngageAppointment: false,
        labType: ["public"],
    });
    const [step, setStep] = useState<AvailabilityStep>(
        screeningFlow === SCREENING_FLOW.HPV ? "hpvService" : "labSelection"
    );
    const [labType, setLabType] = useState<LabType>();
    const [analytics] = useAnalytics();
    const [openModalTrigger, setOpenModalTrigger] = useState<OpenModalTrigger>(
        "page load"
    );
    const [locationType, setLocationType] = useState<LocationType>(
        "postalCode"
    );
    const pendingBookingFlowClinicListLocationRef = useRef<string | null>(null);

    const { translations } = useLanguage();

    const suppressAppointmentReservationConflictNotification = suppressErrorNotificationForTypes(
        [APPOINTMENT_RESERVATION_CONFLICT]
    );

    const fetchClinics = async (body?: FetchClinicsBody) => {
        try {
            const { data } = await Axios.post<Clinic[]>(
                ENDPOINTS.CLINICS,
                body
            );
            setClinics(data);
        } catch (error) {
            NotificationManager.error(error);
        }
    };

    useEffect(() => {
        let clinicListLocationType = "none";

        fetchClinics(fetchClinicsBody);

        if (fetchClinicsBody.geolocation)
            clinicListLocationType = "geolocation";
        if (fetchClinicsBody.originPostalCode)
            clinicListLocationType = "postal code";

        if (!isBookingFlowRedesign) {
            analytics?.cdp()?.trackEvent(UserActionEvent.ClinicListViewed, {
                location: clinicListLocationType,
            });
            return;
        }

        const pendingLocation = pendingBookingFlowClinicListLocationRef.current;
        if (pendingLocation) {
            trackBookingFlowClinicListViewed(analytics, pendingLocation);
            pendingBookingFlowClinicListLocationRef.current = null;
        }
    }, [analytics, fetchClinicsBody, isBookingFlowRedesign]);

    useEffect(() => {
        if (!authUser) {
            push(routes.SIGN_UP);
            return;
        }

        const labType = getLabType(getPreSelectedLabType());
        setFetchClinicsBody((prev) => ({
            ...prev,
            originPostalCode: authUser.zipCode
                ? normalizePostalCode(authUser.zipCode)
                : undefined,
            labType,
        }));
    }, [authUser, push]);

    useEffect(() => {
        if (!location.state?.screeningFlowSwitched) {
            return;
        }

        const flow = getScreeningFlow();
        setStep(flow === SCREENING_FLOW.HPV ? "hpvService" : "labSelection");
        setLabType(undefined);
        setFetchClinicsBody((prev) => ({
            ...prev,
            labType: flow === SCREENING_FLOW.HPV ? ["private"] : ["public"],
        }));
        replace({ pathname: location.pathname, state: undefined });
    }, [location.state, location.pathname, replace]);

    useEffect(() => {
        if (step === "consultation" && !labType) {
            setStep("labSelection");
            return;
        }
        if (
            step === "consultation" &&
            (labType === "private" || getPreSelectedLabType())
        ) {
            setBookingFollowUpType(FollowUpType.Express);
            setStep("availability");
            return;
        }
        if (step !== "availability" || !isBookingFlowRedesign) {
            return;
        }
        if (getScreeningFlow() === SCREENING_FLOW.HPV) {
            return;
        }
        if (getPreSelectedLabType()) {
            if (!getBookingFollowUpType()) {
                setBookingFollowUpType(FollowUpType.Express);
            }
            return;
        }
        if (getBookingFollowUpType()) {
            return;
        }
        setStep("consultation");
    }, [step, isBookingFlowRedesign, labType]);

    // Match screening route: block booking while any missed-appointment fee is unpaid
    if (authUser && !!getAppointmentNeedingMissedPayment(authUser)) {
        return <Redirect to={routes.APPOINTMENTS} />;
    }

    const confirmAppointment = (datetime: string, clinicId: number) => {
        setAppointmentDate(datetime);
        setAppointmentClinicId(clinicId);
        setAppointmentSummaryModalVisible(true);
    };

    const reserveAppointment = async () => {
        try {
            if (appointmentClinicId && appointmentDate) {
                const body = {
                    clinicId: appointmentClinicId,
                    date: appointmentDate.slice(0, 10),
                    time: appointmentDate.slice(11, 16),
                    labType: fetchClinicsBody.labType[0],
                };
                await Axios.put(
                    ENDPOINTS.APPOINTMENT_RESERVE,
                    body,
                    suppressAppointmentReservationConflictNotification
                );
                setAppointmentHold(
                    appointmentClinicId,
                    appointmentDate,
                    selectedClinic?.name
                );
                setPreSelectedClinicID(appointmentClinicId.toString());
                try {
                    const { data } = await Axios.get(ENDPOINTS.ME);
                    setAuthUser(data);
                } catch {
                    // Reserve succeeded; continue even if /me fails so the user is not stuck.
                }
                setAppointmentSummaryModalVisible(false);
                push(authUser ? routes.SCREENING : routes.SIGN_UP);
                if (isBookingFlowRedesign && appointmentClinicId) {
                    trackBookingFlowClinicSelected(
                        analytics,
                        appointmentClinicId
                    );
                }
                if (!isBookingFlowRedesign) {
                    analytics
                        ?.cdp()
                        ?.trackEvent(UserActionEvent.ClinicListSelected, {
                            appointmentClinicId,
                        });
                }
            }
        } catch (error) {
            if (
                Axios.isAxiosError(error) &&
                error.response?.data?.type === APPOINTMENT_RESERVATION_CONFLICT
            ) {
                setAppointmentSummaryModalVisible(false);
                setAppointmentDate(appointmentDate);
                setAppointmentNotAvailableModalVisible(true);
                return;
            }

            NotificationManager.error(error);
        }
    };

    const handleLocationSubmit = (result: LocationModalResult) => {
        const labType = getLabType(getPreSelectedLabType());
        setFetchClinicsBody((prev) => ({
            ...prev,
            originPostalCode: result.postalCode,
            geolocation: result.geolocation,
            labType,
        }));

        if (isBookingFlowRedesign) {
            pendingBookingFlowClinicListLocationRef.current = result.geolocation
                ? "geolocation"
                : "postal code";
        }
    };

    const handleOpenModal = () => {
        setOpenModalTrigger("user action");
        setIsModalOpen(true);
    };

    const selectedClinic =
        clinics && appointmentClinicId
            ? clinics.find((clinic) => {
                  return clinic.id === appointmentClinicId;
              })
            : undefined;

    const handleLabSelectionSubmission = (labType: LabType) => {
        setPreSelectedLabType(labType === "private");
        const normalizedPostalCode = authUser?.zipCode
            ? normalizePostalCode(authUser.zipCode)
            : undefined;

        if (!isBookingFlowRedesign) {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ScreeningAppointmentLabSelection, {
                    lab: labType,
                });
        }
        setLabType(labType);
        setFetchClinicsBody((prev) => ({
            ...prev,
            originPostalCode: normalizedPostalCode,
            geolocation: undefined,
            labType: [labType],
        }));
        if (isBookingFlowRedesign && screeningFlow !== SCREENING_FLOW.HPV) {
            if (labType === "private") {
                setBookingFollowUpType(FollowUpType.Express);
                trackBookingFlowConsultationSelected(
                    analytics,
                    FollowUpType.Express
                );
                setStep("availability");
            } else {
                removeBookingFollowUpType();
                setStep("consultation");
            }
        } else {
            removeBookingFollowUpType();
            setStep("availability");
        }
    };

    const handleConsultationSubmission = (followUpType: FollowUpType) => {
        setBookingFollowUpType(followUpType);
        setStep("availability");
    };

    const handleHpvServiceSubmission = () => {
        setPreSelectedLabType(true);
        setStep("availability");
    };

    const handleLabTypeSelection = (labType: LabType) => {
        setPreSelectedLabType(labType === "private");
        if (!isBookingFlowRedesign) {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ScreeningAppointmentLabSelection, {
                    lab: labType,
                });
        }
        setLabType(labType);
        setFetchClinicsBody((prev) => ({
            ...prev,
            labType: [labType],
        }));
    };

    if (step !== "availability") {
        if (step === "labSelection") {
            return (
                <LabSelectionPage
                    labType={labType}
                    setLabType={setLabType}
                    handleLabSelectionSubmission={handleLabSelectionSubmission}
                />
            );
        }
        if (step === "consultation") {
            if (!labType) {
                return null;
            }
            return (
                <ConsultationSelectionV2
                    labType={labType}
                    onSubmit={handleConsultationSubmission}
                />
            );
        }
        return <HpvServicePage onSubmit={handleHpvServiceSubmission} />;
    }

    const isHpvScreeningFlow = getScreeningFlow() === SCREENING_FLOW.HPV;
    const bookingFollowUpType = getBookingFollowUpType();
    const isPrivateLabBooking = getPreSelectedLabType();
    const effectiveFollowUpType =
        bookingFollowUpType ??
        (isPrivateLabBooking ? FollowUpType.Express : null);

    if (
        isBookingFlowRedesign &&
        !isHpvScreeningFlow &&
        !effectiveFollowUpType
    ) {
        return null;
    }

    const clinicAvailabilityContent = (
        <div className="availability__container">
            <div>
                <h3 className="availability__title">
                    {translations.availabilityPublicPage.title}
                </h3>
                <p className="availability__description">
                    {fetchClinicsBody.originPostalCode ||
                    fetchClinicsBody.geolocation ? (
                        <>
                            <div>
                                {
                                    translations.availabilityPublicPage
                                        .description[
                                        fetchClinicsBody.geolocation
                                            ? "geolocation"
                                            : "postalCode"
                                    ]
                                }
                                <button
                                    onClick={handleOpenModal}
                                    className="availability__location-button"
                                >
                                    {fetchClinicsBody.geolocation
                                        ? translations.availabilityPublicPage
                                              .description.you
                                        : fetchClinicsBody.originPostalCode}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleOpenModal}
                                className="availability__location-button"
                            >
                                {
                                    translations.availabilityPublicPage
                                        .description.enterYourLocation
                                }
                            </button>{" "}
                            <span>
                                {
                                    translations.availabilityPublicPage
                                        .description.closestToYou
                                }
                            </span>
                        </>
                    )}{" "}
                </p>
            </div>

            {!isHpvScreeningFlow && !isBookingFlowRedesign && (
                <>
                    <h3 className="availability__section-title">
                        {translations.availabilityPublicPage.screeningType}
                    </h3>

                    <div className="availability__turnaround-options">
                        <LabSelectionButton
                            t={
                                translations.availabilityPublicPage
                                    .turnaroundFilter
                            }
                            labType="public"
                            isSelected={labType === "public"}
                            onSelect={() => {
                                handleLabTypeSelection("public");
                            }}
                        />
                        <LabSelectionButton
                            t={
                                translations.availabilityPublicPage
                                    .turnaroundFilter
                            }
                            labType="private"
                            isSelected={labType === "private"}
                            onSelect={() => {
                                handleLabTypeSelection("private");
                            }}
                        />
                    </div>
                </>
            )}

            <AvailabilityLocationModal
                labType={labType}
                setLabType={setLabType}
                postalCode={authUser?.zipCode}
                isModalOpen={isModalOpen}
                openModalTrigger={openModalTrigger}
                setIsModalOpen={setIsModalOpen}
                onSubmitLocation={handleLocationSubmit}
                locationType={locationType}
                setLocationType={setLocationType}
                useBookingFlowLocationEvents={isBookingFlowRedesign}
            />

            <AppointmentNotAvailableModal
                appointmentDate={appointmentDate}
                visible={appointmentNotAvailableModalVisible}
                onClose={() => setAppointmentNotAvailableModalVisible(false)}
            />

            <h3 className="availability__section-title">
                {translations.availabilityPublicPage.yourCity}
            </h3>

            {appointmentDate && selectedClinic && (
                <AppointmentSummaryModal
                    appointmentDate={appointmentDate}
                    appointmentClinic={selectedClinic}
                    visible={appointmentSummaryModalVisible}
                    onClose={() => setAppointmentSummaryModalVisible(false)}
                    onSubmit={reserveAppointment}
                />
            )}

            <DateSelection
                onSubmit={confirmAppointment}
                fetchClinicsBody={fetchClinicsBody}
                loading={false}
                selectedFollowUpType={
                    isBookingFlowRedesign
                        ? effectiveFollowUpType ?? FollowUpType.Regular
                        : FollowUpType.Regular
                }
                showTitle={false}
                trackBookingFlowClinicListFilter={isBookingFlowRedesign}
            />
        </div>
    );

    if (isBookingFlowRedesign) {
        return (
            <BookingFlowLayout
                currentStep="clinic"
                skipConsultationStep={isHpvScreeningFlow || isPrivateLabBooking}
                pageClassName={isModalOpen ? "modal-open" : undefined}
                legacyBodyContent
            >
                {clinicAvailabilityContent}
            </BookingFlowLayout>
        );
    }

    return (
        <div className={`page ${isModalOpen ? "modal-open" : ""}`}>
            <Header />
            {clinicAvailabilityContent}
            <Footer />
        </div>
    );
}

function LabSelectionButton({
    labType,
    t,
    onSelect,
    isSelected,
}: {
    labType: "public" | "private";
    t: Translations["availabilityPublicPage"]["turnaroundFilter"];
    onSelect: (labType: "public" | "private") => void;
    isSelected: boolean;
}) {
    return (
        <FilterOptionButton
            option={t[labType].option}
            isSelected={isSelected}
            onSelect={() => {
                onSelect(labType);
            }}
        >
            <span className="availability__turnaround-option-price">
                {t[labType].price}
            </span>
        </FilterOptionButton>
    );
}
